import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from 'react-i18next';
import { notify } from '@/lib/toast';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Factory, Play, CheckCircle2, XCircle,
  Package, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Dialog } from 'primereact/dialog';
import { useAuthStore } from '@/stores/auth.store';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { cn, formatCurrency } from '@/lib/utils';

interface CompleteRunBody {
  actualYield: number;
  notes?: string;
}

interface RunMaterialProduct {
  name?: string;
}

interface RunMaterialItem {
  id: string;
  uom?: string | null;
  quantity?: number | string | null;
  plannedQuantity?: number | string | null;
  actualQuantity?: number | string | null;
  usedQuantity?: number | string | null;
  rawMaterialProduct?: RunMaterialProduct | null;
  product?: RunMaterialProduct | null;
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  DRAFT:       { label: 'Draft',       color: 'bg-muted text-muted-foreground' },
  SCHEDULED:   { label: 'Scheduled',   color: 'bg-info/10 text-info-text' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-warning/10 text-warning-text' },
  COMPLETED:   { label: 'Completed',   color: 'bg-success/10 text-success-text' },
  ON_HOLD:     { label: 'On Hold',     color: 'bg-primary/10 text-primary-text' },
  CANCELLED:   { label: 'Cancelled',   color: 'bg-destructive/10 text-destructive-text' },
};

export function ProductionRunDetailPage() {
  const { t } = useTranslation('restaurant');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('production:update') || hasPermission('production:*') || hasPermission('*');

  const [completeOpen, setCompleteOpen] = useState(false);
  const [actualYield, setActualYield] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');

  const { data: run, isLoading } = useQuery({
    queryKey: ['production-run', id],
    queryFn: () => api.get(`/production-runs/${id}`).then(r => r.data),
    enabled: !!id,
  });

  const startMutation = useMutation({
    mutationFn: () => api.patch(`/production-runs/${id}/start`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-run', id] }); toast.success('Production run started'); },
    onError: (e) => notify.error(e, 'Failed to start'),
  });

  const completeMutation = useMutation({
    mutationFn: (body: CompleteRunBody) => api.patch(`/production-runs/${id}/complete`, body).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-run', id] });
      qc.invalidateQueries({ queryKey: ['production-runs'] });
      toast.success('Production run completed! Stock updated.');
      setCompleteOpen(false);
    },
    onError: (e) => notify.error(e, 'Failed to complete run'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.patch(`/production-runs/${id}/cancel`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-run', id] }); toast.success('Run cancelled'); },
    onError: () => toast.error('Failed to cancel'),
  });

  const handleComplete = () => {
    const qty = Number(actualYield);
    if (!qty || qty <= 0) { toast.error('Enter actual yield quantity'); return; }
    completeMutation.mutate({ actualYield: qty, notes: completionNotes || undefined });
  };

  if (isLoading) return (
    <div className="space-y-6 page-enter">
      <Button variant="ghost" size="sm" onClick={() => navigate('/manufacturing/production')}><ArrowLeft className="h-4 w-4 me-1" />Back</Button>
      <SkeletonTable rows={6} cols={4} />
    </div>
  );

  if (!run) return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <Factory className="h-12 w-12 text-muted-foreground/40 mb-4" />
      <p className="font-medium text-muted-foreground">Production run not found</p>
      <Button className="mt-4" onClick={() => navigate('/manufacturing/production')}>Back to Production</Button>
    </div>
  );

  const cfg = STATUS_CONFIG[run.status] ?? STATUS_CONFIG.DRAFT;
  const items: RunMaterialItem[] = run.consumedMaterials ?? run.recipe?.items ?? [];
  const variancePct = Number(run.variancePercent ?? 0);

  return (
    <div className="space-y-6 page-enter">
      <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate('/manufacturing/production')}>
        <ArrowLeft className="h-4 w-4" /> Production
      </Button>

      <PageHeader
        title={run.runNumber}
        subtitle={`${run.recipe?.name} → ${run.recipe?.finishedProduct?.name} · ${run.location?.name}`}
        action={
          canWrite && (
            <div className="flex gap-2 flex-wrap">
              {run.status === 'SCHEDULED' && (
                <Button className="gap-2" onClick={() => startMutation.mutate()} disabled={startMutation.isPending}>
                  <Play className="h-4 w-4" /> {startMutation.isPending ? 'Starting…' : 'Start Production'}
                </Button>
              )}
              {run.status === 'IN_PROGRESS' && (
                <Button className="gap-2" onClick={() => { setActualYield(String(run.scheduledQuantity)); setCompleteOpen(true); }}>
                  <CheckCircle2 className="h-4 w-4" /> Mark Complete
                </Button>
              )}
              {['SCHEDULED', 'ON_HOLD'].includes(run.status) && (
                <Button variant="outline" className="gap-2 text-destructive-text border-destructive/40 hover:bg-destructive/5"
                  onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending}>
                  <XCircle className="h-4 w-4" /> Cancel Run
                </Button>
              )}
            </div>
          )
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Status</p>
          <span className={cn('text-xs font-semibold px-2.5 py-1 rounded-full', cfg.color)}>{cfg.label}</span>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Planned Qty</p>
          <p className="text-lg font-bold">{run.scheduledQuantity}</p>
        </CardContent></Card>
        {run.actualYield != null && (
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Actual Yield</p>
            <p className="text-lg font-bold">{run.actualYield}</p>
          </CardContent></Card>
        )}
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Planned Cost</p>
          <p className="text-lg font-bold">{formatCurrency(Number(run.plannedCost ?? 0))}</p>
        </CardContent></Card>
        {run.actualCost != null && (
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Actual Cost</p>
            <p className="text-lg font-bold">{formatCurrency(Number(run.actualCost ?? 0))}</p>
          </CardContent></Card>
        )}
        {variancePct !== 0 && (
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Variance</p>
            <p className={cn('text-lg font-bold', variancePct > 5 ? 'text-destructive-text' : 'text-success-text')}>
              {variancePct > 0 ? '+' : ''}{variancePct.toFixed(1)}%
            </p>
          </CardContent></Card>
        )}
      </div>

      {/* Variance warning */}
      {run.status === 'COMPLETED' && variancePct > 10 && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-warning/30 bg-warning/10 text-warning-text">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">High yield variance detected</p>
            <p className="text-xs mt-0.5">Actual yield deviated {variancePct.toFixed(1)}% from plan. Review ingredient consumption and process efficiency.</p>
          </div>
        </div>
      )}

      {/* Bill of materials used */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4" /> Raw Materials Consumed
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No ingredient data available</p>
          ) : (
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('tblIngredient')}</Th>
                  <Th className="text-end">{t('tblPlanned')}</Th>
                  {run.status === 'COMPLETED' && <Th className="text-end">{t('tblActualUsed')}</Th>}
                  <Th>{t('tblUOM')}</Th>
                </Tr>
              </THead>
              <TBody>
                {items.map((item: RunMaterialItem) => {
                  const product = item.rawMaterialProduct ?? item.product;
                  const planned = Number(item.plannedQuantity ?? item.quantity ?? 0) * Number(run.scheduledQuantity ?? 1);
                  const actual = Number(item.actualQuantity ?? item.usedQuantity ?? 0);
                  return (
                    <Tr key={item.id}>
                      <Td label={t('tblIngredient')} className="text-sm font-medium">{product?.name ?? '—'}</Td>
                      <Td label={t('tblPlanned')} className="text-sm text-end font-mono">{planned.toFixed(3)}</Td>
                      {run.status === 'COMPLETED' && <Td label={t('tblActualUsed')} className="text-sm text-end font-mono">{actual.toFixed(3)}</Td>}
                      <Td label={t('tblUOM')} className="text-sm text-muted-foreground">{item.uom}</Td>
                    </Tr>
                  );
                })}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {run.notes && (
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Notes</p>
          <p className="text-sm">{run.notes}</p>
        </CardContent></Card>
      )}

      {/* Complete Dialog */}
      <Dialog
        visible={completeOpen}
        onHide={() => setCompleteOpen(false)}
        header="Mark Run Complete"
        modal
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        style={{ width: '40vw' }}
        footer={
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Cancel</Button>
            <Button onClick={handleComplete} disabled={completeMutation.isPending}>
              <CheckCircle2 className="h-4 w-4 me-1" />
              {completeMutation.isPending ? 'Completing…' : 'Complete & Update Stock'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 py-2">
          <div className="p-3 rounded-lg bg-info/10 border border-info/30">
            <p className="text-sm text-info-text">
              Completing this run will deduct raw materials from stock and add finished goods.
            </p>
          </div>
          <div>
            <Label className="mb-1 block">Actual Yield Quantity *</Label>
            <Input type="number" min="0.001" step="0.001"
              value={actualYield}
              onChange={e => setActualYield(e.target.value)}
              placeholder={`Planned: ${run.scheduledQuantity}`}
            />
            <p className="text-xs text-muted-foreground mt-1">Planned quantity: {run.scheduledQuantity} {run.recipe?.yieldUOM}</p>
          </div>
          <div>
            <Label className="mb-1 block">Completion Notes (optional)</Label>
            <Textarea
              className="resize-none"
              rows={2} value={completionNotes}
              onChange={e => setCompletionNotes(e.target.value)}
              placeholder="Any observations, quality notes…"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
